import { addQuarters, endOfQuarter, isWithinInterval, startOfQuarter } from "date-fns";
import type { ExpectedCloseV2CompanyForecast } from "../expected-close-v2/localExpectedCloseForecast";
import { getExpectedCloseV2Timing, getSelectedPeriodMonths } from "../forecast-diagnosis/forecastDiagnosisContract";
import type { ForecastDiagnosisStory } from "../forecast-diagnosis/forecastDiagnosisStory";

export const FORECAST_MANAGEMENT_POLICY = { minimumPeriodProbability: .3, targetShare: .05, largestShare: .25, limit: 3 } as const;

/** Approved Forecast-only selection, before preview limits; no model/forecast recalculation. */
export function selectForecastManagementPaths(story: ForecastDiagnosisStory, artifact: ExpectedCloseV2CompanyForecast,
  openDeals: readonly { id: number | string; closed_at?: string | Date | null; is_closed?: boolean; is_won?: boolean; is_lost?: boolean }[],
  range: { start: Date; end: Date }, target: number) {
  if (!(target > 0)) return [];
  const open = new Map(openDeals.map(deal => [String(deal.id), deal]));
  const predictions = new Map(artifact.dealForecasts.map(deal => [String(deal.dealId), deal]));
  const terminal = new Set(artifact.terminalBuyerOutcomeDeals?.map(deal => String(deal.dealId)));
  const months = getSelectedPeriodMonths(range.start, range.end);
  const lastMonth = [...months].sort().at(-1)!;
  const following = { start: addQuarters(startOfQuarter(range.end), 1), end: endOfQuarter(addQuarters(range.end, 1)) };
  const quarterSelection = range.start.getTime() === startOfQuarter(range.start).getTime()
    && range.end.getTime() === endOfQuarter(range.start).getTime();
  const eligible = story.deals.flatMap(deal => {
    const source = open.get(deal.id);
    const prediction = predictions.get(deal.id);
    if (!source || source.is_closed || source.is_won || source.is_lost || !prediction || terminal.has(deal.id)) return [];
    const values = prediction.wonProbabilityByMonth.map(([, probability]) => probability);
    if (values.some(probability => !Number.isFinite(probability) || probability < 0 || probability > 1)
      || values.reduce((sum, probability) => sum + probability, 0) > 1 + 1e-10) return [];
    const probability = getExpectedCloseV2Timing(prediction, months, lastMonth)?.inPeriodProbability;
    if (probability === undefined || probability + 1e-10 < FORECAST_MANAGEMENT_POLICY.minimumPeriodProbability) return [];
    const crmDate = source.closed_at ? new Date(source.closed_at) : null;
    if (!crmDate || !Number.isFinite(crmDate.getTime())) return [];
    const current = isWithinInterval(crmDate, range);
    if (!current && !(quarterSelection && isWithinInterval(crmDate, following))) return [];
    if (!Number.isFinite(deal.upsideDelta) || deal.upsideDelta <= 0) return [];
    return [{ ids: [deal.id], dealId: deal.id, dealIds: [deal.id], dealName: deal.name, dealCount: 1,
      impact: deal.upsideDelta, eventType: current ? "won" as const : "moved-in" as const,
      condition: current ? "Complete the win within the selected period" : "Bring the win forward from the following quarter",
      stage: deal.stage, valueExplanation: "Additional revenue if won within the period, above the contribution already included.",
      probability, crmCloseDate: crmDate.toISOString() }];
  }).sort((a, b) => b.impact - a.impact || b.probability - a.probability || a.dealId.localeCompare(b.dealId));
  const minimum = Math.max(target * FORECAST_MANAGEMENT_POLICY.targetShare,
    (eligible[0]?.impact ?? 0) * FORECAST_MANAGEMENT_POLICY.largestShare);
  return eligible.filter(path => path.impact + 1e-10 >= minimum).slice(0, FORECAST_MANAGEMENT_POLICY.limit);
}
